import router from "./router";
import { NavigationGuardNext, RouteLocationNormalized } from "vue-router";

// modules
import AuthModule from "@/store/modules/auth";
import UserModule from "@/store/modules/user";
import POSConfigModule from "@/store/modules/pos-config";
import ModalModule from "@/store/modules/modal";

// helpers
import {
  getExpiresData,
  getUserAccessToken,
  getUserRefreshToken,
  removeUserTokens,
} from "@/utils/helpers/auth";
import { ensurePosClientInit } from "@/utils/pos-client-init";
import { ModalStatus } from "@/constants/modal";

// const
const TIME_BEFORE_AUTO_REFRESH = 900000;
const whiteList = ["/login"];

/**
 * Registers the router navigation guard.
 * Must be called after the router is initialized (from main.ts),
 * not at module evaluation time, to avoid circular dependency issues
 * where this module gets evaluated before the router is created.
 */
export function setupPermissionGuard() {
  router.beforeEach(
    (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      const accessToken: string | undefined = getUserAccessToken();

      // determine whether the user has logged in
      if (accessToken) {
        // update the token if necessary
        updateToken()
          .then(async () => {
            const token = AuthModule.token;
            const userId = UserModule.userId;

            // set token in state
            if (!token) AuthModule.setAccessToken(accessToken);

            // get user data
            if (!userId) {
              if (from.name !== "login" && to.name === "login") {
                // Do nothing here to avoid having resError which will redirect to login again and will repeat the scenario infinitly
              } else {
                await UserModule.fetchUser();
              }
            }

            // Init @smeetz/pos-client (i18n + modelkit) once tokens are loaded.
            await ensurePosClientInit();

            // if pos config is not selected => even after reloading the page, still leave on the login page
            // with an open modal to select pos config and only after selecting pos config redirect to the home page
            if (POSConfigModule.getPosConfigPermission) {
              if (whiteList.indexOf(to.path) !== -1) {
                // if pages from the whitelist => redirect to the home page
                next({ path: "/", replace: true });
              } else {
                // go directly to the pages
                next();
              }
            } else {
              ModalModule.setStatus(ModalStatus.PosConfig);

              if (whiteList.indexOf(to.path) !== -1) {
                // go directly to the pages from the whitelist
                next();
              } else {
                // other pages that do not have permission to access => redirected to the login page.
                next({ path: "/login" });
              }
            }
          })
          .catch(() => {
            if (whiteList.indexOf(to.path) !== -1) {
              // go directly to the pages from the whitelist
              next();
            } else {
              // other pages that do not have permission to access => redirected to the login page.
              next({ path: "/login" });
            }
          });
      } else {
        // has no token
        if (whiteList.indexOf(to.path) !== -1) {
          // go directly to the pages from the whitelist
          next();
        } else {
          // other pages that do not have permission to access => redirected to the login page.
          next({ path: "/login" });
        }
      }
    }
  );
}

/**
 * Check expires date for token.
 * If the access token expires in less than 15 minutes token will be refreshed
 *
 * @return {Promise}
 */
export async function updateToken() {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<void>(async (resolve, reject) => {
    const refreshToken = getUserRefreshToken();
    const expiresData = getExpiresData();
    const todayMs = Date.now();

    if (expiresData && refreshToken) {
      const expiresDataMs = new Date(expiresData).getTime();

      if (expiresDataMs - todayMs <= TIME_BEFORE_AUTO_REFRESH) {
        AuthModule.setAccessToken("");
        try {
          await AuthModule.SetTokensAfterRefresh();
          resolve();
        } catch {
          reject(new Error("Failed to set token after refresh"));
        }
      } else {
        resolve();
      }
    } else {
      removeUserTokens();
      resolve();
      // reject("no token");
    }
  });
}
